/**
 * B-7202: the PRE-download review dialog behind the Supplement page's
 * "Download ESX" button (founder, 2026-09-30), split out of V2SupplementPage.
 *
 * Opted out (or no stored parse to summarise) → straight to the export as
 * before. Otherwise the dialog opens and is filled from the server's summary of
 * the stored parse (Review edits included), so what it lists is what the file
 * will carry. Download with "Don't show this again" unticked sets
 * `emailAfterExportRef`; the page's export reads it once it has the export's
 * run_ref, so the email names THIS export.
 */
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { BackendAPI } from '../../lib/api';
import type { ExportReviewNote } from '../../types';
import type { ExportReviewEntry, PasteCheck } from '../../types/exportSummary';
import ExportCompleteDialog from '../components/ExportCompleteDialog';

interface PreExportState {
  parseHash: string;
  loading: boolean;
  steps?: string[];
  reviewNotes?: ExportReviewNote[];
  reviewEntries?: ExportReviewEntry[];
  pasteCheck?: PasteCheck | null;
  reviewCount?: number;
}

/** Emails the summary for one finished export; never throws, never blocks. */
export function sendExportSummaryEmail(parseHash: string, runRef: string): void {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    tz = undefined; // the server falls back to Eastern, labelled ET
  }
  void BackendAPI.sendExportSummaryEmail(parseHash, runRef, tz).catch(() => {});
}

/** The pre-download dialog's state, handlers and element for one page. */
export function usePreExportDialog({
  parseHash,
  download,
  openReview,
  emailAfterExportRef,
}: {
  parseHash: string | undefined;
  /** The page's existing export entry point (cost confirm, then export). */
  download: () => void;
  /** Switch the page to its Review view. */
  openReview: () => void;
  emailAfterExportRef: MutableRefObject<boolean>;
}) {
  const [preExport, setPreExport] = useState<PreExportState | null>(null);
  // Source line numbers the last summary flagged: "Go to review" lists these in
  // the Review view beside the unmapped lines.
  const [reviewFlagged, setReviewFlagged] = useState<Set<number>>(() => new Set());
  // The user's "Don't show this again" opt-out, loaded once per mount. A failed
  // read resolves to false (show the dialog; the server re-checks before it
  // emails anything).
  const hideExportDialogRef = useRef<Promise<boolean> | null>(null);
  const loadHideExportDialog = useCallback(() => {
    if (!hideExportDialogRef.current) {
      hideExportDialogRef.current = BackendAPI.getExportPreferences()
        .then((p) => !!p?.hide_export_dialog)
        .catch(() => false);
    }
    return hideExportDialogRef.current;
  }, []);
  useEffect(() => {
    void loadHideExportDialog();
  }, [loadHideExportDialog]);

  const openPreExport = useCallback(async () => {
    emailAfterExportRef.current = false;
    if (!parseHash || (await loadHideExportDialog())) {
      download();
      return;
    }
    setPreExport({ parseHash, loading: true });
    BackendAPI.getExportSummary(parseHash)
      .then((sum) =>
        setPreExport((prev) =>
          prev && prev.parseHash === parseHash
            ? {
                ...prev,
                loading: false,
                steps: sum.steps,
                reviewNotes: sum.review_notes ?? [],
                reviewEntries: sum.review_entries,
                pasteCheck: sum.paste_check,
                reviewCount: sum.review_count,
              }
            : prev,
        ),
      )
      // A failed summary never blocks the export: the dialog still downloads.
      .catch(() => setPreExport((prev) => (prev ? { ...prev, loading: false } : prev)));
  }, [parseHash, loadHideExportDialog, download, emailAfterExportRef]);

  const onDownload = useCallback(
    (dontShowAgain: boolean) => {
      setPreExport(null);
      if (dontShowAgain) {
        hideExportDialogRef.current = Promise.resolve(true);
        void BackendAPI.setExportPreferences({ hide_export_dialog: true }).catch(() => {});
      }
      emailAfterExportRef.current = !dontShowAgain;
      download();
    },
    [download, emailAfterExportRef],
  );

  const onGoToReview = useCallback(() => {
    const flagged = new Set<number>();
    for (const n of preExport?.reviewNotes ?? []) {
      if (n.line_number != null) flagged.add(n.line_number);
    }
    setReviewFlagged(flagged);
    setPreExport(null);
    openReview();
  }, [preExport, openReview]);

  const dialog = preExport ? (
    <ExportCompleteDialog
      loading={preExport.loading}
      steps={preExport.steps}
      reviewNotes={preExport.reviewNotes}
      reviewEntries={preExport.reviewEntries}
      pasteCheck={preExport.pasteCheck}
      reviewCount={preExport.reviewCount}
      onDownload={onDownload}
      onGoToReview={onGoToReview}
      onCancel={() => setPreExport(null)}
    />
  ) : null;

  return { openPreExport, reviewFlagged, dialog };
}
